import type { NextId } from "./step";

function len(n: number, decimals = 6): string {
  if (!Number.isFinite(n)) return "0.";
  const s = n.toFixed(decimals).replace(/\.?0+$/, "");
  return s.includes(".") ? s : `${s}.`;
}

/**
 * Creates a small cylinder centered at the element's local origin (0,0,0).
 * IMPORTANT:
 * - This geometry is in the element's local coordinate system.
 * - The element's IFCLOCALPLACEMENT controls the world position.
 */
export function createMarkerCylinder(args: {
  getNextId: NextId;
  geometricContextId: number;
  radiusM?: number;   // meters
  heightM?: number;   // meters
}): { productShapeId: number; output: string } {
  const { getNextId, geometricContextId } = args;
  const radius = args.radiusM ?? 0.10; // 10 cm default
  const height = args.heightM ?? 0.20; // 20 cm default

  // 2D profile placement
  const pt2dId = getNextId();
  const ax2dId = getNextId();
  const profileId = getNextId();

  // 3D solid placement (local)
  const pt3dId = getNextId();
  const zAxisId = getNextId();
  const xAxisId = getNextId();
  const ax3dId = getNextId();

  // Extrusion
  const extrudeDirId = getNextId();
  const solidId = getNextId();

  // Representation
  const shapeRepId = getNextId();
  const productShapeId = getNextId();

  let output = "";

  // IfcCircleProfileDef needs a 2D axis placement
  output += `#${pt2dId}=IFCCARTESIANPOINT((0.,0.));\n`;
  output += `#${ax2dId}=IFCAXIS2PLACEMENT2D(#${pt2dId},$);\n`;
  output += `#${profileId}=IFCCIRCLEPROFILEDEF(.AREA.,'Marker',#${ax2dId},${len(radius)});\n`;

  // Solid positioned at local origin
  output += `#${pt3dId}=IFCCARTESIANPOINT((0.,0.,0.));\n`;
  output += `#${zAxisId}=IFCDIRECTION((0.,0.,1.));\n`;
  output += `#${xAxisId}=IFCDIRECTION((1.,0.,0.));\n`;
  output += `#${ax3dId}=IFCAXIS2PLACEMENT3D(#${pt3dId},#${zAxisId},#${xAxisId});\n`;

  output += `#${extrudeDirId}=IFCDIRECTION((0.,0.,1.));\n`;
  output += `#${solidId}=IFCEXTRUDEDAREASOLID(#${profileId},#${ax3dId},#${extrudeDirId},${len(height)});\n`;

  // Body representation (SweptSolid)
  output += `#${shapeRepId}=IFCSHAPEREPRESENTATION(#${geometricContextId},'Body','SweptSolid',(#${solidId}));\n`;
  output += `#${productShapeId}=IFCPRODUCTDEFINITIONSHAPE($,$,(#${shapeRepId}));\n`;

  return { productShapeId, output };
}
